/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * DropDownDepartment — tree BP/NV header.
 * isAdmin/isQuanLy lấy qua getPhanHeFlags(profile, useGetPhanHe()) — đúng lát PH đang mở.
 */
import { HEADER_ROUTES, NOT_ALLOW_EMPLOYEE_ROUTES, shouldHideEmployeeInTreeHeader, shouldUseVanHoaCompanyHeader, TREE_SELECT_ROUTES } from '@/configs/ROUTE_HEADER';
import { usePrivateStore } from '@/store/usePrivateStore';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useRef, useState } from 'react';
import DepEmpTreeSelect from '../common/treeSelectV2/header/useDepEmpTreeSelect';
import useOrganizationIndexedDB from '@/hooks/useOrganizationIndexedDB';
import {
  findAncestorCompanyDepartment,
  isCompanyDepartment,
  getScopedOrganizationFromArrays,
  isTreeSelectInScopedOrg,
  resolveTreeSelectionForModule,
  type ResolvedTreeSelection,
  type TreeSelectSnapshot,
} from '@/utils/orgScope';
import { useOrgScope } from '@/contexts/OrgScopeContext';
import { resolveEffectivePathname } from '@/components/PhanHe/VanHoaPhanHeHandler';
import CommonJs from '@/utils/commonJs';
import { syncTreeSelectDepartmentFromIdBoPhan, TREE_SELECT_DEPARTMENT_SYNCED_EVENT } from '@/components/common/ActionPlanDepartment/navigateCongViecDuAnFromDepartment';
import { useGetPhanHe } from '@/hooks/useGetPhanHe';
import { getPhanHeFlags } from '@/utils/phanHeRoles';

/**
 * DropDownDepartment Component
 *
 * Hiển thị dropdown chọn bộ phận/nhân viên dựa trên quyền của user
 * - Nhân viên: chỉ xem được bộ phận của mình
 * - Quản lý: xem được bộ phận và nhân viên theo quyền
 * - Admin: xem được toàn bộ
 */
interface DropDownDepartmentProps {
    profile: any;
    tenantID: string;
}

export const DropDownDepartment: React.FC<DropDownDepartmentProps> = ({ profile, tenantID }) => {
    const router = useRouter();
    const checkBangKeHoach = router.query.param || '';
    const {
        departmentId,
        setDepartmentId,
        setTreeSelectDepartment,
        clearTreeSelectDepartment,
        treeSelectDepartment,
    } = usePrivateStore();
    const {  getCompanys, getSubOrganization } = useOrganizationIndexedDB();
    const [companyData, setCompanyData] = useState<any | null>(null);
    const [loading, setLoading] = useState(true);
    const [reloadKey, setReloadKey] = useState(0);
    const { orgScope, controller } = useOrgScope();
    const activePhanHe = useGetPhanHe();
    const phanHeFlags = getPhanHeFlags(profile, activePhanHe);
    const hasFullOrgAccess = orgScope.useFullOrg;
    const hasManagerAccess = orgScope.treatAsQuanLy;
    const isPlainEmployee =
        Boolean(profile?.id) &&
        !phanHeFlags.isAdmin &&
        !phanHeFlags.isQuanLy &&
        !hasFullOrgAccess &&
        !hasManagerAccess;
    //const isQuanLy = hasManagerAccess;
    const isNhanVien = isPlainEmployee && !hasFullOrgAccess && !hasManagerAccess;
    // const isAdmin = profile?.isAdmin;
    //const isQuanLy = profile?.isQuanLy;
    //const isNhanVien = !isQuanLy && !isAdmin;
    const effectivePathname = resolveEffectivePathname(router.pathname, router.asPath);
    const isVanHoaRoute = effectivePathname.startsWith('/van-hoa');
    /** Chỉ trang VH (trừ Đào tạo & Action Plan) — không áp dụng cho BSC. */
    const useVanHoaCompanyHeader = isVanHoaRoute && shouldUseVanHoaCompanyHeader(effectivePathname);

    // Listen for IndexedDB reload event
    useEffect(() => {
        const handleIndexedDBReloaded = () => {
            setReloadKey((prev) => prev + 1);
        };

        window.addEventListener('indexedDBReloaded', handleIndexedDBReloaded);
        return () => {
            window.removeEventListener('indexedDBReloaded', handleIndexedDBReloaded);
        };
    }, []);

    useEffect(() => {
        const fetchCompanyData = async () => {
            try {
                setLoading(true);
                const companyData = await getCompanys();
                setCompanyData(companyData);

                //check link bảng kế hoạch 
                if(checkBangKeHoach) {
                    const calculatedDepartmentId =
                    !phanHeFlags.isQuanLy && !phanHeFlags.isAdmin ? `emp_${profile?.id}_dep_${profile?.boPhanId}` : profile?.boPhanId;
                    const allDeps = companyData?.departments;
                    const itemSelected = allDeps?.find((dept: any) => dept.ID === calculatedDepartmentId);

                    if(checkBangKeHoach === 'ct') {
                        const isCompanyId = itemSelected?.IsCompany;
                        
                        if (!isCompanyId && allDeps?.length > 0) {
                            const Parent = allDeps?.find((dept: any) => dept.ID === itemSelected.ParentID);

                            if(Parent?.IsCompany) {
                                setSelectedDepartment({
                                    value: Parent.ID,
                                    label: Parent.Ten,
                                });
                                setDepartmentId(Parent.ID);
                                setTreeSelectDepartment({ 
                                    selectedValue: {
                                        value: Parent.ID,
                                        label: Parent.Ten,
                                    },
                                    tenantID: tenantID || '',
                                    idSelect: Parent.ID,
                                    isEmployee: false,
                                    parentID: Parent.ParentID,
                                    isCompany: true,
                                });
                            } else {
                                const congTyArray = allDeps?.filter((dept: any) => !(dept.IsCompany));
                                if(congTyArray?.length > 0) {
                                    const congTy = congTyArray[0];
                                    const congTyId = String(congTy.ID ?? congTy.id ?? '');
                                    setSelectedDepartment({
                                        value: congTyId,
                                        label: congTy.Ten ?? congTy.ten ?? '',
                                    });
                                    setDepartmentId(congTyId);
                                    setTreeSelectDepartment({
                                        selectedValue: {
                                            value: congTyId,
                                            label: congTy.Ten ?? congTy.ten ?? '',
                                        },
                                        tenantID: tenantID || '',
                                        idSelect: congTyId,
                                        isEmployee: false,
                                        parentID: congTy.ParentID ?? congTy.parentID ?? '0',
                                        isCompany: true,
                                    });
                                }
                            }
                        }
                        else {
                            setSelectedDepartment({
                                value: itemSelected?.ID,
                                label: itemSelected?.Ten ?? itemSelected?.ten ?? '',
                            });
                            setDepartmentId(itemSelected?.ID);
                            setTreeSelectDepartment({
                                selectedValue: {
                                    value: itemSelected?.ID,
                                    label: itemSelected?.Ten ?? itemSelected?.ten ?? '',
                                },
                                tenantID: tenantID || '',
                                idSelect: itemSelected?.ID,
                                isEmployee: false,
                                parentID: itemSelected?.ParentID ?? itemSelected?.parentID ?? '0',
                                isCompany: itemSelected?.IsCompany,
                            });
                        }
                    } 
                    else if(checkBangKeHoach === 'pb') {
                        // Check if departmentId is a department
                        const isDepartment = itemSelected?.IsCompany !== true && !calculatedDepartmentId.includes('_nv');
                        
                        // If not a department, get the first department that is not company (IsCompany !== true)
                        if (!isDepartment && allDeps?.length > 0) {
                            let phongBanArray = allDeps?.filter((dept: any) => !(dept.IsCompany));
                            const isCompanyId = itemSelected?.IsCompany;
                            // Nếu calculatedDepartmentId là công ty thì ưu tiên lấy phòng ban con trực tiếp của công ty đó
                            if (isCompanyId) {
                                phongBanArray = phongBanArray?.filter(
                                    (dept: any) =>
                                        String(dept.ParentID) === String(calculatedDepartmentId)
                                );
                            } 

                            if (phongBanArray?.length > 0) {
                                const firstDept = phongBanArray[0];
                                const firstDeptId = String(firstDept.ID ?? firstDept.id ?? '');
                                
                                if (firstDeptId && firstDeptId !== '0') {
                                    setTimeout(() => {
                                        setSelectedDepartment({
                                            value: firstDeptId,
                                            label: firstDept.Ten ?? firstDept.ten ?? '',
                                        });
                                        setDepartmentId(firstDeptId);
                                        setTreeSelectDepartment({
                                            selectedValue: {
                                                value: firstDeptId,
                                                label: firstDept.Ten ?? firstDept.ten ?? '',
                                            },
                                            tenantID: tenantID || '',
                                            idSelect: firstDeptId,
                                            isEmployee: false,
                                            parentID: firstDept.ParentID ?? firstDept.parentID ?? '0',
                                            isCompany: false,
                                        });
                                    }, 400);
                                }
                            }
                        }
                    }
                    else if(checkBangKeHoach === 'nv') {
                        const isNhanVien = CommonJs.checkIsEmployee(calculatedDepartmentId);
                        if(!isNhanVien) {
                            const allEmps = (companyData?.employees || []).filter(
                                (e: any) => !(e?.IsQuanLy ?? e?.isQuanLy)
                            );
                            // Check if departmentId is a department
                            const listEmps = allEmps?.filter((emp: any) => emp.BPID === calculatedDepartmentId 
                                && emp.NghiViec !== true && emp.PhanHe?.includes('bsc'));
                            
                            // If not a department, get the first department that is not company (IsCompany !== true)
                            if (listEmps?.length > 0) {
                                const firstEmp = listEmps[0];
                                const firstEmpId = String(firstEmp.ID ?? firstEmp.id ?? '');
                                const bpOri = allDeps.find((depart: any) => depart.ID === firstEmp?.BPID) as any;

                                // Nếu calculatedDepartmentId là công ty thì ưu tiên lấy phòng ban con trực tiếp của công ty đó
                                if (firstEmpId && firstEmpId !== '0') {
                                    setTimeout(() => {
                                        setDepartmentValues(bpOri, {
                                            isEmployee: true,
                                            employeeId: firstEmpId,
                                            employeeName: firstEmp.Ten ?? firstEmp.ten ?? '',
                                        });
                                        setSelectedDepartment({
                                            value: firstEmpId,
                                            label: firstEmp.Ten ?? firstEmp.ten ?? '',
                                        });
                                        setDepartmentId(firstEmpId);
                                        setTreeSelectDepartment({
                                            selectedValue: {
                                                value: firstEmpId,
                                                label: firstEmp.Ten ?? firstEmp.ten ?? '',
                                            },
                                            tenantID: tenantID || '',
                                            idSelect: firstEmpId,
                                            isEmployee: true,
                                            parentID: firstEmp.ParentID ?? firstEmp.parentID ?? '0',
                                            isCompany: false,
                                        });
                                    }, 400);
                                }
                            }
                        }
                    }
                }
            } catch (error: any) {
                console.error('Error getting company data:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchCompanyData();
    }, [getCompanys, reloadKey, checkBangKeHoach]);


    // VH (trừ Đào tạo/AP): label tạm theo BP user — effect sẽ set công ty cha
    const calculatedDepartmentId = useVanHoaCompanyHeader
        ? profile?.boPhanId
        : isPlainEmployee
            ? `emp_${profile?.id}_dep_${profile?.boPhanId}`
            : profile?.boPhanId;

    const departmentLabel = useVanHoaCompanyHeader
        ? profile?.tenBoPhan
        : isPlainEmployee
            ? profile?.tenNhanVien
            : profile?.tenBoPhan;

    // State quản lý bộ phận được chọn
    const [selectedDepartment, setSelectedDepartment] = useState<{ value: string; label: string } | undefined>({
        value: calculatedDepartmentId || '',
        label: departmentLabel || '',
    });
    // Lấy dữ liệu tree từ global store — đọc fresh trong effect qua getState()

    /** Đồng bộ label/value header khi store đổi từ URL (cong-viec-du-an) hoặc navigate department. */
    useEffect(() => {
        const sv = treeSelectDepartment?.selectedValue;
        if (!sv?.value) return;
        setSelectedDepartment(sv);
        if (treeSelectDepartment.idSelect) {
            setDepartmentId(treeSelectDepartment.idSelect);
        }
    }, [
        treeSelectDepartment.selectedValue,
        treeSelectDepartment.idSelect,
        treeSelectDepartment.tenantID,
        setDepartmentId,
    ]);

    useEffect(() => {
        const onSynced = () => {
            const synced = usePrivateStore.getState().getTreeSelectDepartment();
            const sv = synced?.selectedValue;
            if (!sv?.value) return;
            setSelectedDepartment(sv);
            if (synced.idSelect) setDepartmentId(synced.idSelect);
        };
        window.addEventListener(TREE_SELECT_DEPARTMENT_SYNCED_EVENT, onSynced);
        return () => window.removeEventListener(TREE_SELECT_DEPARTMENT_SYNCED_EVENT, onSynced);
    }, [setDepartmentId]);

    /**
     * Kiểm tra route có được phép hiển thị nhân viên không
     */
    const canShowEmployee = (route: string) => {
        return !NOT_ALLOW_EMPLOYEE_ROUTES.includes(route);
    };

    const availableHeader = TREE_SELECT_ROUTES.includes(controller ?? '');

    // Văn hóa: chọn theo bộ phận thực tế của user (không ép cấp công ty như BSC buoc1-4).
    const isCompanyOnlyRoute = HEADER_ROUTES.CONGTY_ROUTES.includes(controller ?? '');
    const hideEmployeeForApv2Manager = shouldHideEmployeeInTreeHeader(
        effectivePathname,
        router.query as Record<string, string | string[] | undefined>,
    );
    const isShowEmployee =
        !isCompanyOnlyRoute &&
        canShowEmployee(controller ?? '') &&
        !hideEmployeeForApv2Manager;
    /** VH admin công ty: dùng lại cây routeCongTy giống BSC buoc1. */
    const useVhAdminCompanyTree = useVanHoaCompanyHeader && hasFullOrgAccess;
    const useVhSingleCompanyTree = useVanHoaCompanyHeader && !hasFullOrgAccess;

    const viewModeConfig = useMemo(() => ({
        VuotCap: orgScope.roleVuotCap,
        NgangCap: orgScope.roleNgangCap,
        Congty: isCompanyOnlyRoute,
    }), [orgScope.roleVuotCap, orgScope.roleNgangCap, isCompanyOnlyRoute]);

    const viewModeString = useMemo(() => {
        return [
            `ngangcap:${viewModeConfig.NgangCap}`,
            `vuotcap:${viewModeConfig.VuotCap}`,
            `congty:${viewModeConfig.Congty}`,
        ].join(',');
    }, [viewModeConfig]);

    /**
     * Xử lý khi user thay đổi lựa chọn bộ phận
     */
    const handleDepartmentChange = (value: { value: string; label: string } | undefined) => {
        setSelectedDepartment(value);
        setDepartmentId(value?.value || null);
    };

    /**
     * Khởi tạo departmentId nếu chưa có
     */
    useEffect(() => {
        if (calculatedDepartmentId && !departmentId) {
            setDepartmentId(calculatedDepartmentId);
        }
    }, [calculatedDepartmentId, departmentId, setDepartmentId]);

    //// Ref để theo dõi sự thay đổi của viewModeConfig
    //const prevViewModeConfigRef = useRef({
    //    VuotCap: roleVuotCap,
    //    NgangCap: roleAllBP,
    //    Congty: roleAllCT,
    //});
    //// Rule có thay đổi
    //const [isViewModeChange, setIsViewModeChange] = useState(false);

    //// Theo dõi sự thay đổi của viewModeConfig
    //useEffect(() => {
    //    const prevConfig = prevViewModeConfigRef.current;
    //    const currentConfig = {
    //        VuotCap: roleVuotCap,
    //        NgangCap: roleAllBP,
    //        Congty: roleAllCT,
    //    };

    //    const hasChanged =
    //        prevConfig.VuotCap !== currentConfig.VuotCap ||
    //        prevConfig.NgangCap !== currentConfig.NgangCap ||
    //        prevConfig.Congty !== currentConfig.Congty;

    //    setIsViewModeChange(hasChanged);
    //    prevViewModeConfigRef.current = currentConfig;
    //}, [roleVuotCap, roleAllBP, roleAllCT]);

    const vhCompanyHeaderInitRef = useRef(false);
    const vhDeptHeaderInitRef = useRef(false);
    const prevIsVanHoaRouteRef = useRef(isVanHoaRoute);
    const prevUseVanHoaCompanyHeaderRef = useRef(useVanHoaCompanyHeader);

    useEffect(() => {
        const wasVanHoa = prevIsVanHoaRouteRef.current;
        prevIsVanHoaRouteRef.current = isVanHoaRoute;
        if (wasVanHoa && !isVanHoaRoute) {
            vhCompanyHeaderInitRef.current = false;
            vhDeptHeaderInitRef.current = false;
            setReloadKey((k) => k + 1);
        }
    }, [isVanHoaRoute]);

    useEffect(() => {
        const wasCompanyHeader = prevUseVanHoaCompanyHeaderRef.current;
        prevUseVanHoaCompanyHeaderRef.current = useVanHoaCompanyHeader;
        if (wasCompanyHeader && !useVanHoaCompanyHeader && isVanHoaRoute) {
            vhDeptHeaderInitRef.current = false;
            setReloadKey((k) => k + 1);

            /** Trưởng phòng / nhân viên: reset cây tổ chức ngay (giống BSC). Admin giữ nguyên. */
            if (!hasFullOrgAccess && companyData?.departments) {
                const global = usePrivateStore.getState().getTreeSelectDepartment();
                if (global.isCompany && profile?.boPhanId) {
                    const allDeps = Object.values(companyData.departments || {});
                    const bpOri = allDeps.find((depart: any) => depart.ID === profile.boPhanId) as any;
                    if (bpOri) {
                        if (isNhanVien && isShowEmployee) {
                            syncVanHoaDeptHeaderSelection(bpOri, {
                                isEmployee: true,
                                employeeId: profile?.id,
                                employeeName: profile?.tenNhanVien,
                            });
                        } else {
                            syncVanHoaDeptHeaderSelection(bpOri);
                        }
                    }
                }
            }
        }
    }, [useVanHoaCompanyHeader, isVanHoaRoute, hasFullOrgAccess, companyData, profile?.boPhanId, profile?.id, profile?.tenNhanVien, isNhanVien, isShowEmployee]);

    /** Đồng bộ UI header từ store (không ghi store). */
    const syncLocalFromTreeStore = () => {
        const current = usePrivateStore.getState().getTreeSelectDepartment();
        const sv = current?.selectedValue;
        if (!sv?.value || !current.idSelect) return;
        if (selectedDepartment?.value !== sv.value || selectedDepartment?.label !== sv.label) {
            setSelectedDepartment(sv);
        }
        const nextId = String(current.idSelect);
        if (String(departmentId ?? '') !== nextId) {
            setDepartmentId(nextId);
        }
    };

    const applyResolvedSelection = (resolved: ResolvedTreeSelection) => {
        setDepartmentId(resolved.idSelect);
        setTreeSelectDepartment({
            selectedValue: resolved.selectedValue,
            tenantID: tenantID || '',
            idSelect: resolved.idSelect,
            isEmployee: resolved.isEmployee,
            parentID: resolved.parentID,
            isCompany: resolved.isCompany,
        });
        setSelectedDepartment(resolved.selectedValue);
    };

    /** Kiểm tra selection theo quyền module; invalid → reset theo profile/userID. */
    const ensureSelectionInModuleScope = (
        global: TreeSelectSnapshot,
        allDeps: any[],
        allEmps: any[],
    ): boolean => {
        if (hasFullOrgAccess) return true;
        const resolved = resolveTreeSelectionForModule(
            profile,
            global,
            allDeps,
            allEmps,
            orgScope,
            { isShowEmployee },
        );
        if (resolved) {
            applyResolvedSelection(resolved);
            return false;
        }
        return true;
    };

    /**
     * VH: set công ty gần nhất — không clear store (tránh reset idSelect khi chuyển trang VH).
     */
    const syncVanHoaCompanyHeaderSelection = (companyDept: any) => {
        if (!companyDept) return;

        const companyId = String(companyDept.ID ?? companyDept.id ?? '');
        const label = String(companyDept.Ten ?? companyDept.ten ?? '');
        if (!companyId || !label) return;

        const selectedValue = { value: companyId, label };
        const tenant = tenantID || '';
        const current = usePrivateStore.getState().getTreeSelectDepartment();

        if (
            current.tenantID === tenant
            && String(current.idSelect ?? '') === companyId
            && current.isCompany
            && current.selectedValue?.value === companyId
        ) {
            if (selectedDepartment?.value !== companyId) {
                setSelectedDepartment(selectedValue);
            }
            if (String(departmentId ?? '') !== companyId) {
                setDepartmentId(companyId);
            }
            return;
        }

        setDepartmentId(companyId);
        setTreeSelectDepartment({
            selectedValue,
            tenantID: tenant,
            idSelect: companyId,
            isEmployee: false,
            parentID: String(companyDept.ParentID ?? companyDept.parentID ?? '0'),
            isCompany: isCompanyDepartment(companyDept),
        });
        setSelectedDepartment(selectedValue);
    };

    /** VH: set bộ phận / nhân viên header đồng bộ (tránh page gọi API với id công ty trước khi setDepartmentValues xong). */
    const syncVanHoaDeptHeaderSelection = (
        department: any,
        options?: {
            isEmployee?: boolean;
            employeeId?: string;
            employeeName?: string;
        },
    ) => {
        if (!department) return;

        const deptId = String(department.ID ?? department.id ?? '');
        const deptLabel = String(department.Ten ?? department.ten ?? '');
        if (!deptId || !deptLabel) return;

        const { isEmployee = false, employeeId, employeeName } = options || {};
        const tenant = tenantID || '';
        const current = usePrivateStore.getState().getTreeSelectDepartment();

        if (isEmployee && employeeId) {
            const empValue = `emp_${employeeId}_dep_${deptId}`;
            const selectedValue = { value: empValue, label: employeeName || '' };
            const nextIdSelect = String(employeeId);

            if (
                current.tenantID === tenant
                && String(current.idSelect ?? '') === nextIdSelect
                && current.isEmployee
                && !current.isCompany
                && current.selectedValue?.value === empValue
            ) {
                syncLocalFromTreeStore();
                return;
            }

            setDepartmentId(nextIdSelect);
            setTreeSelectDepartment({
                selectedValue,
                tenantID: tenant,
                idSelect: nextIdSelect,
                isEmployee: true,
                parentID: String(department.ParentID ?? department.parentID ?? '0'),
                isCompany: false,
            });
            setSelectedDepartment(selectedValue);
            return;
        }

        const selectedValue = { value: deptId, label: deptLabel };

        if (
            current.tenantID === tenant
            && String(current.idSelect ?? '') === deptId
            && !current.isCompany
            && current.selectedValue?.value === deptId
        ) {
            syncLocalFromTreeStore();
            return;
        }

        setDepartmentId(deptId);
        setTreeSelectDepartment({
            selectedValue,
            tenantID: tenant,
            idSelect: deptId,
            isEmployee: false,
            parentID: String(department.ParentID ?? department.parentID ?? '0'),
            isCompany: isCompanyDepartment(department),
        });
        setSelectedDepartment(selectedValue);
    };

    /**
     * Helper function để set giá trị cho tree select và selected department
     */
    const setDepartmentValues = (
        department: any,
        options?: {
            isEmployee?: boolean;
            employeeId?: string;
            employeeName?: string;
            customDepartmentId?: string;
        }
    ) => {

        const { isEmployee = false, employeeId, employeeName, customDepartmentId } = options || {};
        const tenant = tenantID || '';
        const current = usePrivateStore.getState().getTreeSelectDepartment();

        const syncIfUnchanged = (
            nextIdSelect: string,
            nextSelectedValue: { value: string; label: string },
            nextIsEmployee: boolean,
            nextParentId: string,
            nextIsCompany: boolean,
        ) => {
            if (
                current.tenantID === tenant
                && String(current.idSelect ?? '') === nextIdSelect
                && current.selectedValue?.value === nextSelectedValue.value
                && current.selectedValue?.label === nextSelectedValue.label
                && current.isEmployee === nextIsEmployee
                && String(current.parentID ?? '0') === nextParentId
                && current.isCompany === nextIsCompany
            ) {
                if (selectedDepartment?.value !== nextSelectedValue.value) {
                    setSelectedDepartment(nextSelectedValue);
                }
                if (String(departmentId ?? '') !== nextIdSelect) {
                    setDepartmentId(nextIdSelect);
                }
                return true;
            }
            return false;
        };

        if (useVanHoaCompanyHeader && department && !isEmployee) {
            const nextDeptId = String(department.ID ?? department.id ?? '');
            const label = String(department.Ten ?? department.ten ?? '');
            if (nextDeptId && label) {
                const nextSelectedValue = { value: nextDeptId, label };
                const parentId = String(department.ParentID ?? department.parentID ?? '0');
                if (syncIfUnchanged(nextDeptId, nextSelectedValue, false, parentId, isCompanyDepartment(department))) {
                    return;
                }
            }
        } else if (isEmployee && employeeId && department) {
            const empValue = `emp_${employeeId}_dep_${department.ID}`;
            const nextSelectedValue = {
                value: empValue,
                label: employeeName || '',
            };
            const parentId = String(department.ParentID ?? department.parentID ?? '0');
            if (syncIfUnchanged(employeeId, nextSelectedValue, true, parentId, department?.Key === 1)) {
                return;
            }
        } else if (department) {
            const nextDeptId = String(customDepartmentId || department.ID || department.id || '');
            const label = String(department.Ten ?? department.ten ?? '');
            if (nextDeptId && label) {
                const nextSelectedValue = { value: nextDeptId, label };
                const parentId = String(department.ParentID ?? department.parentID ?? '0');
                if (syncIfUnchanged(nextDeptId, nextSelectedValue, false, parentId, department?.Key === 1)) {
                    return;
                }
            }
        }

        let idSelect: string;
        let selectedValue: { value: string; label: string };
        let deptId: string;

        // Lấy tổ chức theo profile (async)
        const loadOrgData = orgScope.useFullOrg
            ? getCompanys().then((company) => ({
                departments: Array.isArray(company?.departments)
                    ? company.departments
                    : Object.values(company?.departments || {}),
                employees: Array.isArray(company?.employees)
                    ? company.employees
                    : Object.values(company?.employees || {}),
            }))
            : getSubOrganization(profile, viewModeConfig.VuotCap, viewModeConfig.NgangCap, {
                treatAsQuanLy: orgScope.treatAsQuanLy,
            });

        loadOrgData.then((orgData) => {
            if (useVanHoaCompanyHeader && department && !isEmployee) {
                const departmentId = String(department.ID ?? department.id ?? '');
                const label = String(department.Ten ?? department.ten ?? '');
                if (departmentId && label) {
                    idSelect = departmentId;
                    selectedValue = { value: departmentId, label };
                    deptId = departmentId;
                }
            } else if (isEmployee && employeeId && department) {
                // Trường hợp chọn nhân viên
                // Kiểm tra nhân viên có trong orgData không
                const employeeExists = orgData?.employees?.some((emp: any) => {
                    const empId = String(emp.ID ?? emp.id ?? '');
                    return empId === String(employeeId);
                });
                if (employeeExists) {
                    idSelect = employeeId;
                    selectedValue = {
                        value: `emp_${employeeId}_dep_${department.ID}`,
                        label: employeeName || '',
                    };
                    deptId = employeeId;
                }
            } else if (department) {
                // Trường hợp chọn bộ phận
                // Kiểm tra phòng ban có trong orgData không
                const departmentId = customDepartmentId || department.ID || '';
                const departmentExists = orgData?.departments?.some((dept: any) => {
                    const deptId = dept.id || dept.ID || '';
                    return deptId === departmentId;
                });
              
                if (departmentExists && departmentId) {
                  
                    idSelect = departmentId;
                    selectedValue = {
                        value: department.ID || '',
                        label: department.Ten || '',
                    };
                    deptId = department.ID || '';
                }
            }
            ///idSelect đã chọn không có trong dữ liệu cơ cấu của nhân viên
            ///=> set lại theo profile (BSC/QT — VH đã set ở trên, không fallback)
            if (deptId == null || deptId === undefined || deptId == '') {
                if (useVanHoaCompanyHeader) {
                    return;
                }

                if (orgData?.departments.length > 0) {
                    const departments = orgData?.departments?.filter((dept: any) => {
                        const deptId = dept.id || dept.ID || '';
                        return deptId === profile?.boPhanId;
                    });
                    const departmentParents = orgData?.departments?.filter((dept: any) => {
                        const deptId = dept.id || dept.ID || '';
                        return deptId === departments[0].parentID;
                    });
                    department = departmentParents.length > 0 ? departmentParents[0] : departments[0];
                    idSelect = department.ID || '';
                    selectedValue = {
                        value: department.ID || '',
                        label: department.Ten || '',
                    };
                    deptId = idSelect;
                } else {
                    idSelect = profile?.id || '';
                    selectedValue = {
                        value: `emp_${profile?.id}_dep_${profile?.boPhanId}`,
                        label: profile?.tenNhanVien || '',
                    };
                    deptId = idSelect;
                }
            }

            clearTreeSelectDepartment();
            setDepartmentId(deptId);
            setTreeSelectDepartment({
                selectedValue,
                tenantID: tenantID || '',
                idSelect,
                isEmployee,
                parentID: department?.ParentID || '0',
                isCompany: department?.Key === 1,
            });
            setSelectedDepartment(selectedValue);

        }).catch((error) => {
            console.error('Error getting organization:', error);
        });

    };

    // VH header công ty: init một lần — chuyển trang VH (an-le, đánh giá...) không đổi tổ chức
    useEffect(() => {
        if (!useVanHoaCompanyHeader) {
            vhCompanyHeaderInitRef.current = false;
            return;
        }
        if (loading || !companyData || checkBangKeHoach) {
            return;
        }

        const current = usePrivateStore.getState().getTreeSelectDepartment();
        const tenant = tenantID || '';

        if (current.tenantID !== tenant) {
            vhCompanyHeaderInitRef.current = false;
        }

        if (
            current.tenantID === tenant
            && current.idSelect
            && current.isCompany
            && current.selectedValue?.value
        ) {
            syncLocalFromTreeStore();
            vhCompanyHeaderInitRef.current = true;
            return;
        }

        if (vhCompanyHeaderInitRef.current) {
            syncLocalFromTreeStore();
            return;
        }

        const allDeps = Object.values(companyData?.departments || {});
        const bpOri = allDeps.find((depart: any) => depart.ID === profile?.boPhanId) as any;
        if (!bpOri) return;

        const root = allDeps.find((depart: any) => depart.ParentID === '0' || depart.Key === 1) as any;
        const companyDept = hasFullOrgAccess
            ? root
            : ((findAncestorCompanyDepartment(profile?.boPhanId, allDeps) ?? bpOri) as any);
        syncVanHoaCompanyHeaderSelection(companyDept);
        vhCompanyHeaderInitRef.current = true;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading, companyData, useVanHoaCompanyHeader, profile?.boPhanId, tenantID, checkBangKeHoach]);

    // VH Đào tạo / Action Plan: bộ phận user — init một lần, chuyển trang không đổi
    useEffect(() => {
        if (!isVanHoaRoute || useVanHoaCompanyHeader) {
            vhDeptHeaderInitRef.current = false;
            return;
        }
        if (loading || !companyData || checkBangKeHoach) {
            return;
        }

        const global = usePrivateStore.getState().getTreeSelectDepartment();
        const allDeps = Object.values(companyData?.departments || {});
        const bpOri = allDeps.find((depart: any) => depart.ID === profile?.boPhanId) as any;
        if (!bpOri) {
            return;
        }

        /** Admin: giữ tổ chức đang chọn (kể cả công ty từ dashboard VH). */
        if (hasFullOrgAccess) {
            if (
                global.tenantID === tenantID
                && global.idSelect
                && global.selectedValue?.value
            ) {
                syncLocalFromTreeStore();
                vhDeptHeaderInitRef.current = true;
                return;
            }

            if (vhDeptHeaderInitRef.current) {
                syncLocalFromTreeStore();
                return;
            }

            setDepartmentValues(bpOri);
            vhDeptHeaderInitRef.current = true;
            return;
        }

        /** Trưởng phòng / nhân viên: không mang công ty cha từ dashboard VH sang Đào tạo. */
        const isVanHoaCompanyCarryover = global.isCompany && profile?.boPhanId;

        if (isVanHoaCompanyCarryover) {
            if (isNhanVien && isShowEmployee) {
                syncVanHoaDeptHeaderSelection(bpOri, {
                    isEmployee: true,
                    employeeId: profile?.id,
                    employeeName: profile?.tenNhanVien,
                });
                setDepartmentValues(bpOri, {
                    isEmployee: true,
                    employeeId: profile?.id,
                    employeeName: profile?.tenNhanVien,
                });
            } else {
                syncVanHoaDeptHeaderSelection(bpOri);
                setDepartmentValues(bpOri);
            }
            vhDeptHeaderInitRef.current = true;
            return;
        }

        if (
            global.tenantID === tenantID
            && global.idSelect
            && global.selectedValue?.value
            && !global.isCompany
        ) {
            const allEmps = Object.values(companyData?.employees || {});
            if (ensureSelectionInModuleScope(global, allDeps, allEmps)) {
                syncLocalFromTreeStore();
            }
            vhDeptHeaderInitRef.current = true;
            return;
        }

        if (vhDeptHeaderInitRef.current) {
            syncLocalFromTreeStore();
            return;
        }

        if (isNhanVien && isShowEmployee) {
            setDepartmentValues(bpOri, {
                isEmployee: true,
                employeeId: profile?.id,
                employeeName: profile?.tenNhanVien,
            });
        } else {
            setDepartmentValues(bpOri);
        }
        vhDeptHeaderInitRef.current = true;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading, companyData, isVanHoaRoute, useVanHoaCompanyHeader, hasFullOrgAccess, profile?.boPhanId, profile?.id, profile?.tenNhanVien, tenantID, checkBangKeHoach, isNhanVien, isShowEmployee]);

    // BSC / Quy trình — luồng gốc, tách biệt hoàn toàn khỏi VH
    useEffect(() => {
        if (isVanHoaRoute || loading || !companyData || checkBangKeHoach) {
            return;
        }

        /** APV2 muc-tieu: ưu tiên idBoPhan/treeSelectValue trên URL — không ghi đè bằng profile. */
        const isApv2Path =
            effectivePathname === '/action-plan-v2' ||
            effectivePathname.startsWith('/action-plan-v2/');
        if (isApv2Path) {
            const urlTree = String(
                (typeof router.query.treeSelectValue === 'string' && router.query.treeSelectValue) ||
                    (typeof router.query.idBoPhan === 'string' && router.query.idBoPhan) ||
                    ''
            ).trim();
            if (urlTree && urlTree !== '0') {
                const global = usePrivateStore.getState().getTreeSelectDepartment();
                const urlIdBoPhan = String(
                    typeof router.query.idBoPhan === 'string' ? router.query.idBoPhan : ''
                ).trim();
                const matched =
                    String(global.selectedValue?.value ?? '').trim() === urlTree ||
                    String(global.idSelect ?? '').trim() === urlTree ||
                    (!!urlIdBoPhan &&
                        urlIdBoPhan !== '0' &&
                        String(global.idSelect ?? '').trim() === urlIdBoPhan);
                if (matched) {
                    syncLocalFromTreeStore();
                    return;
                }
                void syncTreeSelectDepartmentFromIdBoPhan(
                    urlTree,
                    tenantID,
                    setTreeSelectDepartment,
                    true
                ).then((ok) => {
                    if (ok) syncLocalFromTreeStore();
                });
                return;
            }
        }

        const global = usePrivateStore.getState().getTreeSelectDepartment();
        const allDeps = Object.values(companyData?.departments || {});
        const root = allDeps.find((depart: any) => depart.ParentID === '0' || depart.Key === 1) as any;
        const isEmployeeNode = global?.selectedValue?.value?.includes?.('emp_')|| global?.selectedValue?.value?.endsWith?.('_nv') || false;

        const bpOri = allDeps.find((depart: any) => depart.ID === profile?.boPhanId) as any;
        if (!bpOri) {
            return;
        }

        // buoc5: NV (không admin / quản lý) mặc định hiển thị đúng user đang login
        if (controller === 'buoc5' && isNhanVien && profile?.id) {
            setDepartmentValues(bpOri, {
                isEmployee: true,
                employeeId: profile.id,
                employeeName: profile.tenNhanVien,
            });
            return;
        }

        /** VH header công ty cha — không mang sang BSC (dashboard, báo cáo, ...). */
        const isVanHoaCompanyCarryover =
            global.isCompany
            && profile?.boPhanId
            && String(global.idSelect ?? '') !== String(profile.boPhanId);

        if (isVanHoaCompanyCarryover && bpOri && isVanHoaRoute) {
            if (isCompanyOnlyRoute) {
                const companyDept = hasFullOrgAccess
                    ? root
                    : (findAncestorCompanyDepartment(profile.boPhanId, allDeps) ?? bpOri);
                setDepartmentValues(companyDept);
            } else {
                setDepartmentValues(bpOri);
            }
            return;
        }

        const currentID = isEmployeeNode
            ? global?.selectedValue?.value?.split?.('_dep_')[1]
            : global?.selectedValue?.value;

        const currentGet = allDeps.find(
            (depart: any) => String(depart.ID ?? depart.id ?? '') === String(currentID ?? ''),
        ) as any;

        // Route không cho chọn NV: emp từ trang khác → map về bộ phận của NV đó
        if (
            !isCompanyOnlyRoute
            && isEmployeeNode
            && !isShowEmployee
            && global.tenantID === tenantID
            && global.selectedValue?.value
        ) {
            if (currentGet) {
                setDepartmentValues(currentGet);
            } else if (isPlainEmployee && viewModeConfig.VuotCap && bpOri) {
                setDepartmentValues(bpOri);
            } else {
                clearTreeSelectDepartment();
                setDepartmentId('');
                setSelectedDepartment(undefined);
            }
            return;
        }

        // Route bộ phận: đã chọn → giữ nếu còn trong phạm vi quyền module
        if (
            !isCompanyOnlyRoute
            && global.tenantID === tenantID
            && global.idSelect
            && global.selectedValue?.value
            && !global.isCompany
        ) {
            const allEmps = Object.values(companyData?.employees || {});
            if (ensureSelectionInModuleScope(global, allDeps, allEmps)) {
                syncLocalFromTreeStore();
            }
            return;
        }

        console.log('isCompanyOnlyRoute ==> ', isCompanyOnlyRoute);
        console.log('global ==> ', global);
        
        // 1. ROUTE CÔNG TY
        if (isCompanyOnlyRoute) {
            if (global.isCompany && !isVanHoaCompanyCarryover) {
                setDepartmentValues(allDeps.find((d: any) => d.ID === global.idSelect) as any);
                return;
            }
            if (hasFullOrgAccess) {
                setDepartmentValues(root);
                return;
            }

            const profileBP = allDeps.find((d: any) => d.ID === profile?.boPhanId) as any;
            if (profileBP?.Key === 1) {
                setDepartmentValues(profileBP);
                return;
            }

            if (viewModeConfig.VuotCap) {
                const parentId = String(profileBP?.ParentID ?? profileBP?.parentID ?? '0');
                const parent = parentId && parentId !== '0'
                    ? allDeps.find((d: any) => String(d.ID ?? d.id) === parentId) as any
                    : undefined;
                if (parent && (parent.Key === 1 || isCompanyDepartment(parent))) {
                    setDepartmentValues(parent);
                    return;
                }
                const ancestor = findAncestorCompanyDepartment(profile?.boPhanId, allDeps);
                if (ancestor) {
                    setDepartmentValues(ancestor);
                    return;
                }
            }

            setDepartmentValues(null);
            return;
        }

        // 2. ROUTE BỘ PHẬN — admin: giữ store, không gọi setDepartmentValues mỗi lần đổi trang
        if (global.tenantID == tenantID) {
            if (hasFullOrgAccess) {
                if (!isShowEmployee) {
                    if (isEmployeeNode && currentGet) {
                        setDepartmentValues(currentGet);
                    } else if (global.idSelect && global.selectedValue?.value) {
                        syncLocalFromTreeStore();
                    } else {
                        setDepartmentValues(currentGet);
                    }
                } else {
                    clearTreeSelectDepartment();
                    setDepartmentId(global?.idSelect || '');
                    setTreeSelectDepartment({
                        selectedValue: {
                            value: global?.selectedValue?.value || '',
                            label: global?.selectedValue?.label || '',
                        },
                        tenantID: tenantID || '',
                        idSelect: global?.idSelect || '',
                        isEmployee: global?.isEmployee || false,
                        parentID: global?.parentID || '0',
                        isCompany: global?.isCompany || false,
                    });
                    setSelectedDepartment({
                        value: global?.selectedValue?.value || '',
                        label: global?.selectedValue?.label || '',
                    });
                }
                return;
            }
        }

        if (isPlainEmployee) {
            const currentValue = global?.selectedValue?.value;
            const isEmpNode = currentValue && typeof currentValue === 'string' && currentValue.startsWith('emp_');

            if (isEmpNode && !isShowEmployee) {
                if (currentGet) {
                    setDepartmentValues(currentGet);
                } else if (viewModeConfig.VuotCap) {
                    setDepartmentValues(bpOri);
                } else {
                    clearTreeSelectDepartment();
                    setDepartmentId('');
                    setSelectedDepartment(undefined);
                }
                return;
            }

            if (!currentValue && isShowEmployee) {
                setDepartmentValues(bpOri, {
                    isEmployee: true,
                    employeeId: profile?.id,
                    employeeName: profile?.tenNhanVien,
                });
                return;
            }

            if (currentValue) {
                if (isEmpNode && isShowEmployee) {
                    setDepartmentValues(bpOri, {
                        isEmployee: true,
                        employeeId: CommonJs.getEmpIDFromEmpDepKey(currentValue) || profile?.id,
                        employeeName: profile?.tenNhanVien,
                    });
                    return;
                }
                if (!isEmpNode) {
                    setDepartmentValues(bpOri);
                    return;
                }
            }

            if (isShowEmployee) {
                setDepartmentValues(bpOri, {
                    isEmployee: true,
                    employeeId: profile?.id,
                    employeeName: profile?.tenNhanVien,
                });
            } else if (viewModeConfig.VuotCap) {
                setDepartmentValues(bpOri);
            } else {
                clearTreeSelectDepartment();
                setDepartmentId('');
                setSelectedDepartment(undefined);
            }
        } else {
            const allEmps = Object.values(companyData?.employees || {});
            const resolved = resolveTreeSelectionForModule(
                profile,
                global,
                allDeps,
                allEmps,
                orgScope,
                { isShowEmployee },
            );
            if (resolved) {
                if (resolved.isEmployee) {
                    setDepartmentValues(bpOri, {
                        isEmployee: true,
                        employeeId: resolved.idSelect,
                        employeeName: resolved.selectedValue.label,
                    });
                } else {
                    setDepartmentValues(allDeps.find((d: any) => d.ID === resolved.idSelect) ?? bpOri);
                }
                return;
            }

            const scoped = getScopedOrganizationFromArrays(profile, allDeps, allEmps, orgScope);
            if (isEmployeeNode && isShowEmployee) {
                setDepartmentValues(bpOri, {
                    customDepartmentId: global.parentID || '0',
                    isEmployee: global.isEmployee,
                    employeeId: global.idSelect || '',
                    employeeName: global.selectedValue?.label || '',
                });
            } else if (currentGet && isTreeSelectInScopedOrg(global, scoped)) {
                setDepartmentValues(currentGet);
            } else {
                setDepartmentValues(bpOri);
            }
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [controller, loading, companyData, isVanHoaRoute, checkBangKeHoach, orgScope, isShowEmployee, hasFullOrgAccess, isNhanVien]);

    // Hiển thị loading hoặc ẩn component nếu không cần thiết
    if (loading) {
        return <div className="w-[200px] h-8 bg-gray-100 animate-pulse rounded"></div>;
    }
    if (!controller) return;
    if (!availableHeader) {
        return <></>;
    }

    return (
        <DepEmpTreeSelect
            key={`${controller}-${orgScope.useFullOrg}-${orgScope.treatAsQuanLy}-${orgScope.roleVuotCap}-${orgScope.roleNgangCap}-${isShowEmployee ? 'emp' : 'dept'}`}
            tenantID={tenantID || ''}
            externalValue={
                loading || !companyData
                    ? undefined
                    : selectedDepartment?.label?.trim() &&
                        selectedDepartment.label !== selectedDepartment.value
                      ? selectedDepartment
                      : undefined
            }
            placeholder="Chọn bộ phận..."
            onChange={handleDepartmentChange}
            initialId={departmentId || ''}
            autoSelectFirst={!useVanHoaCompanyHeader}
            isShowEmployee={useVanHoaCompanyHeader ? false : isShowEmployee}
            companyData={companyData}
            routeCongTy={isCompanyOnlyRoute || useVhAdminCompanyTree}
            vanHoaCompanyOnlyTree={useVhSingleCompanyTree}
            viewMode={viewModeString}
        />
    );
};
export const setDefaultDepartment = async (profile: any, tenantID: string) => {
  try {
    if (!profile?.boPhanId || !tenantID) {
      return;
    }

    // Lấy dữ liệu company từ IndexedDB
    const { getOrganizationIndexedDB } = await import('@/utils/organizationIndexedDBUtils');
    const organizationData = await getOrganizationIndexedDB();

    if (!organizationData?.departments) {
      return;
    }

    // Chuyển đổi departments từ object/array sang array
    const allDeps = Array.isArray(organizationData.departments)
      ? organizationData.departments
      : Object.values(organizationData.departments || {});

    // Tìm bộ phận của profile
    const profileBP = allDeps.find((d: any) => d.ID === profile?.boPhanId) as any;

    const { getPhanHeFromStorage } = await import('@/utils/phanHeStorage');
    const flags = getPhanHeFlags(profile, getPhanHeFromStorage());
    const isAdmin = flags.isAdmin;
    const isQuanLy = flags.isQuanLy;
    const isNhanVienOnly = !isQuanLy && !isAdmin;

    if (isNhanVienOnly && profile?.id && profile?.boPhanId) {
      const bpOri = profileBP;
      const { setTreeSelectDepartment, setDepartmentId } = usePrivateStore.getState();
      const empId = String(profile.id);
      setDepartmentId(empId);
      setTreeSelectDepartment({
        selectedValue: {
          value: `emp_${empId}_dep_${profile.boPhanId}`,
          label: profile.tenNhanVien || '',
        },
        tenantID: tenantID || '',
        idSelect: empId,
        isEmployee: true,
        parentID: String(bpOri?.ParentID ?? bpOri?.parentID ?? '0'),
        isCompany: false,
      });
      return;
    }

    if (!profileBP) {
      return;
    }

    // Trường hợp: Bộ phận của profile là loại công ty (key == 1)
    if (profileBP?.Key === 1) {
      const { setTreeSelectDepartment, setDepartmentId } = usePrivateStore.getState();
      setDepartmentId(profileBP.ID);
      setTreeSelectDepartment({
        selectedValue: {
          value: profileBP.ID || '',
          label: profileBP.Ten || '',
        },
        tenantID: tenantID || '',
        idSelect: profileBP.ID || null,
        isEmployee: false,
        parentID: profileBP.ParentID || '0',
        isCompany: true,
      });
    }
  } catch (error) {
    console.error('❌ Error setting default department:', error);
  }
};
